export type PortId = "pulseOut" | "emitIn" | "inc" | "reset" | "chargeIn" | "valueOut" | "valueIn" | "operand1In" | "operand2In" | "transformOut" | "transformIn" | "setParamIn" | "delayIn" | "flashIn" | "routeIn" | "routeOut" | "stepIn" | "target" | "envelopeIn" | "envelopeOut" | "pitchQuantizeIn" | "pitchQuantizeOut";
export type PortDir = "in" | "out";
export type SignalType = "pulse" | "value" | "transform" | "envelope" | "pitchQuantize";

interface PortDef {
  dir: PortDir;
  type: SignalType;
}

/**
 * The single source of truth for every node port: its direction and signal
 * type. Connection validation and the panel port UI both read this table, so a
 * port can only be added in one place. A PortId uniquely determines dir/type,
 * which is why the graph can validate an edge without knowing the node kinds.
 */
export const PORTS: Record<PortId, PortDef> = {
  pulseOut: { dir: "out", type: "pulse" },
  emitIn: { dir: "in", type: "pulse" },
  inc: { dir: "in", type: "pulse" },
  reset: { dir: "in", type: "pulse" },
  chargeIn: { dir: "in", type: "pulse" },
  delayIn: { dir: "in", type: "pulse" },
  valueOut: { dir: "out", type: "value" },
  valueIn: { dir: "in", type: "value" },
  operand1In: { dir: "in", type: "value" },
  operand2In: { dir: "in", type: "value" },
  transformOut: { dir: "out", type: "transform" },
  transformIn: { dir: "in", type: "transform" },
  setParamIn: { dir: "in", type: "value" },
  flashIn: { dir: "in", type: "pulse" },
  routeIn: { dir: "in", type: "pulse" },
  routeOut: { dir: "out", type: "pulse" },
  stepIn: { dir: "in", type: "pulse" },
  target: { dir: "in", type: "value" },
  envelopeIn: { dir: "in", type: "envelope" },
  envelopeOut: { dir: "out", type: "envelope" },
  pitchQuantizeIn: { dir: "in", type: "pitchQuantize" },
  pitchQuantizeOut: { dir: "out", type: "pitchQuantize" },
};

/**
 * `param` names which settable target parameter a `setParamIn` value edge
 * overwrites (empty on every other edge). Any arithmetic on the value happens in
 * the value-node graph upstream (op / operand live on the value node), so the
 * edge itself only carries the routing plus, for Param-in, the target name.
 */
export interface Edge {
  id: string;
  from: string;
  fromPort: PortId;
  to: string;
  toPort: PortId;
  param: string;
}

export type EdgeSpec = Omit<Edge, "id" | "param">;

function isPortId(v: unknown): v is PortId {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(PORTS, v);
}

function parseEdge(entry: unknown): Edge | null {
  if (typeof entry !== "object" || entry === null) {
    return null;
  }
  const e = entry as Record<string, unknown>;
  if (typeof e.from !== "string" || typeof e.to !== "string") {
    return null;
  }
  if (!isPortId(e.fromPort) || !isPortId(e.toPort)) {
    return null;
  }
  return {
    id: typeof e.id === "string" ? e.id : crypto.randomUUID(),
    from: e.from,
    fromPort: e.fromPort,
    to: e.to,
    toPort: e.toPort,
    param: typeof e.param === "string" ? e.param : "",
  };
}

/**
 * Directed dataflow edges between placed entities (objects / emitters / value
 * nodes), keyed by stable entity id and port. Edges carry no runtime
 * signal state — propagation lives in the per-frame evaluator (signals.ts); this
 * class only owns the topology and its validation / persistence.
 */
export class ConnectionGraph {
  private edges: Edge[];
  private rev = 0;
  private undoRecorder: ((id: string) => void) | null = null;

  constructor() {
    this.edges = [];
  }

  /**
   * Lets the undo recorder capture an edge's pre-mutation value the moment a
   * local edit touches it. Mutations on the remote-apply / undo-apply paths run
   * with the recorder suppressed, so only genuine local edits accumulate a step.
   */
  setUndoRecorder(recorder: (id: string) => void): void {
    this.undoRecorder = recorder;
  }

  /** Monotonic revision bumped on every topology / param mutation; lets per-selection UI caches invalidate without hooking each mutator. */
  version(): number {
    return this.rev;
  }

  /**
   * Rejects an edge unless it is a valid out→in pair of matching signal type and
   * not a self-connection; `operand1In`, `operand2In`, `valueIn`, `transformIn`,
   * `target`, `envelopeIn` and `pitchQuantizeIn` stay single-connection (a value node takes
   * one input and one operand, a count node shows one value source, an object has one orbit
   * parent, an orbit follows one target value, a timbre takes one envelope and one
   * pitch-quantize source). `setParamIn`
   * is a
   * value-typed fan-in pin (an object accepts many Param-in wires, each overwriting
   * its own parameter), so it is exempt from the single rule. Identical (same
   * endpoints/ports) parallel edges ARE allowed — each gets its own id and its
   * pulse sums — so the UE-style per-pin wiring can run two wires between the same
   * pins (e.g. one contact → two emits).
   */
  canConnect(spec: EdgeSpec): boolean {
    const f = PORTS[spec.fromPort];
    const t = PORTS[spec.toPort];
    if (!f || !t) {
      return false;
    }
    if (spec.from === spec.to) {
      return false;
    }
    if (f.dir !== "out" || t.dir !== "in") {
      return false;
    }
    if (f.type !== t.type) {
      return false;
    }
    if (
      (spec.toPort === "operand1In" || spec.toPort === "operand2In" || spec.toPort === "valueIn" || spec.toPort === "transformIn" || spec.toPort === "target" || spec.toPort === "envelopeIn" || spec.toPort === "pitchQuantizeIn") &&
      this.edgesTo(spec.to, spec.toPort).length > 0
    ) {
      return false;
    }
    return true;
  }

  connect(spec: EdgeSpec): boolean {
    if (!this.canConnect(spec)) {
      return false;
    }
    const edge: Edge = { id: crypto.randomUUID(), param: "", ...spec };
    this.undoRecorder?.(edge.id);
    this.edges.push(edge);
    this.rev++;
    return true;
  }

  /**
   * Sets the target-parameter name a setParamIn edge overwrites. No-op for an
   * unknown edge id.
   */
  setEdgeParam(id: string, param: string): void {
    const edge = this.edges.find((e: Edge): boolean => e.id === id);
    if (edge) {
      this.undoRecorder?.(id);
      edge.param = param;
      this.rev++;
    }
  }

  disconnect(edge: Edge): void {
    this.undoRecorder?.(edge.id);
    this.edges = this.edges.filter((e: Edge): boolean => e.id !== edge.id);
    this.rev++;
  }

  edgesFrom(id: string): Edge[] {
    return this.edges.filter((e: Edge): boolean => e.from === id);
  }

  edgesTo(id: string, port: PortId): Edge[] {
    return this.edges.filter((e: Edge): boolean => e.to === id && e.toPort === port);
  }

  edgesForEntity(id: string): Edge[] {
    return this.edges.filter((e: Edge): boolean => e.from === id || e.to === id);
  }

  all(): readonly Edge[] {
    return this.edges;
  }

  removeEntity(id: string): void {
    if (this.undoRecorder !== null) {
      for (const e of this.edges) {
        if (e.from === id || e.to === id) {
          this.undoRecorder(e.id);
        }
      }
    }
    this.edges = this.edges.filter((e: Edge): boolean => e.from !== id && e.to !== id);
    this.rev++;
  }

  /** Removes a single edge by its id (per-key remote delta apply). No-op for an unknown id. */
  removeById(id: string): void {
    const before = this.edges.length;
    this.edges = this.edges.filter((e: Edge): boolean => e.id !== id);
    if (this.edges.length !== before) {
      this.rev++;
    }
  }

  /**
   * Replaces (or inserts) one edge from its serialized form, preserving its id so
   * a remote per-key upsert lands on the same edge both peers know. Ignores a
   * malformed entry.
   */
  upsertSerialized(entry: unknown): void {
    const edge = parseEdge(entry);
    if (edge === null) {
      return;
    }
    const idx = this.edges.findIndex((e: Edge): boolean => e.id === edge.id);
    if (idx >= 0) {
      this.edges[idx] = edge;
    } else {
      this.edges.push(edge);
    }
    this.rev++;
  }

  clear(): void {
    this.edges = [];
    this.rev++;
  }

  serialize(): unknown {
    return this.edges.map((e: Edge): Edge => ({ id: e.id, from: e.from, fromPort: e.fromPort, to: e.to, toPort: e.toPort, param: e.param }));
  }

  /** One edge's serialized form by id (undo recorder's before-value capture), or null when absent. */
  serializeById(id: string): unknown | null {
    const e = this.edges.find((edge: Edge): boolean => edge.id === id);
    return e ? { id: e.id, from: e.from, fromPort: e.fromPort, to: e.to, toPort: e.toPort, param: e.param } : null;
  }

  restore(raw: unknown): void {
    this.edges = [];
    this.rev++;
    if (!Array.isArray(raw)) {
      return;
    }
    for (const entry of raw) {
      const edge = parseEdge(entry);
      if (edge) {
        this.edges.push(edge);
      }
    }
  }
}

/**
 * Load-time migration: drops any `valueIn` edge landing on a count node. Count no
 * longer exposes a value input (its display role moved to the display node), so a
 * stale edge from an older scene is silently discarded rather than left dangling
 * on a pin the count no longer has.
 */
export function pruneLegacyCountValueIn(graph: ConnectionGraph, isCount: (id: string) => boolean): void {
  for (const edge of [...graph.all()]) {
    if (edge.toPort === "valueIn" && isCount(edge.to)) {
      graph.disconnect(edge);
    }
  }
}
